import {
  Box,
  Flex,
  Heading,
  Link,
  Card,
  CardBody,
  Grid,
  useMediaQuery,
} from "@chakra-ui/react"
import { memo, useState, useEffect } from "react"
import { EducationMeter } from "../features/game/EducationMeter"
import SlideViewer from "../features/game/SlideViewer"
import { CharacterAvatar } from "../features/character/CharacterAvatar"
import { useAvatarEditorModalActions } from "../features/character/useAvatarEditorModal"
import { MentalHealthMeter } from "../features/game/MentalHealthMeter"
import { PhysicalHealthMeter } from "../features/game/PhysicalHealthMeter"
import { useAppSelector } from "../app/hooks"
import {
  selectHasGameStarted,
  selectIsOnStartSlide,
} from "../features/game/gameSlice"
import StartScreen from "./StartScreen"
import DisclaimerScreen from "./DisclaimerScreen"
import CharacterCreationScreen from "./CharacterCreationScreen"
import { usePerfRenderCount } from "../perf/perfTrace"
import {
  PORTRAIT_MQ,
  SIDEWAYS_MQ,
  SIDEWAYS_SMALL_MQ,
  TABLET_LANDSCAPE_MQ,
} from "../components/mediaQueries"

const GameSlidePage: React.FC = () => {
  usePerfRenderCount("GameSlidePage")
  // Two booleans, so the page shell (cards, heading, avatar) does not
  // re-render on every turn; the meters and the slide subscribe on their own.
  const hasGameStarted = useAppSelector(selectHasGameStarted)
  const isOnStartSlide = useAppSelector(selectIsOnStartSlide)
  const [startScreenState, setStartScreenState] = useState<
    "disclaimer" | "character" | "instructions"
  >("disclaimer")

  // Reset start screen state when game resets (turn becomes 0 and history is empty)
  useEffect(() => {
    if (!hasGameStarted) {
      setStartScreenState("disclaimer")
    }
  }, [hasGameStarted])

  // Show start screen only if the game hasn't started (turn === 0 and no
  // history) AND the current slide is the default start slide or unset.
  // Once the game has started, never show the start screen again (prevents
  // flashing during transitions).
  if (!hasGameStarted && isOnStartSlide) {
    if (startScreenState === "disclaimer") {
      return (
        <DisclaimerScreen
          onContinue={() => {
            setStartScreenState("character")
          }}
        />
      )
    }
    if (startScreenState === "character") {
      return (
        <CharacterCreationScreen
          onContinue={() => {
            setStartScreenState("instructions")
          }}
        />
      )
    }
    if (startScreenState === "instructions") {
      return <StartScreen />
    }
  }

  return (
    <Box
      minH="100vh"
      sx={{ "@supports (height: 100dvh)": { minHeight: "100dvh" } }}
      bgGradient="linear(to-br, #ec4899, #db2777, #be185d)"
      p={{ base: 2, md: 4, lg: 6 }}
      display="flex"
      // A card shorter than the screen (upright screens and the tablet held
      // sideways, where it is as tall as its content) sits centred with the
      // pink above and below: the client's choice, 27 October 2026, over a
      // fixed top (so the title moves between a short and a long card) and
      // over the welcome screens' bedroom behind the card. Nothing is ever
      // taller than this box, so centring cannot clip: the card is capped to
      // the viewport and scrolls inside, and the box is min-height, growing
      // with its content rather than cutting it off at the top.
      alignItems="center"
      justifyContent="center"
    >
      {/* One column up to and including 1280px (the tablet in either
          orientation); the avatar card sits beside the game only from 2xl
          (1536px). The game card keeps its 900px cap from lg, so at 1280x800
          it is the same card as before, centred on its own; the tablet held
          sideways widens it to 1180px for its two columns. */}
      <Flex
        direction={{ base: "column", "2xl": "row" }}
        gap={{ base: 4, "2xl": 6 }}
        maxW="1600px"
        w="100%"
        alignItems={{ base: "center", "2xl": "flex-start" }}
        justifyContent="center"
      >
        {/* Main Card - Meters and Slide. Capped to the viewport so meters,
            story and answers always share one screen: the slide image gives
            up height first (SlideViewer), and only if even minimum sizes
            don't fit does the card body scroll internally. */}
        <Card
          bg="white"
          borderRadius="3xl"
          boxShadow="2xl"
          w="100%"
          maxW={{ base: "100%", lg: "900px" }}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          sx={{
            // A real height, not just a cap: the slide image's flex-grow only
            // receives free space if the card's height is definite. vh is the
            // fallback for older WebViews; dvh tracks the visible viewport
            // when the Android URL bar is shown.
            height: {
              base: "calc(100vh - 1rem)",
              md: "calc(100vh - 2rem)",
              lg: "calc(100vh - 3rem)",
            },
            "@supports (height: 100dvh)": {
              height: {
                base: "calc(100dvh - 1rem)",
                md: "calc(100dvh - 2rem)",
                lg: "calc(100dvh - 3rem)",
              },
            },
            // Upright screens (phones and the tablet; the picture is in
            // normal flow there, src/index.css): as tall as the content, up
            // to the same cap, so a short card ends under its last button.
            [PORTRAIT_MQ]: {
              height: "auto",
              maxHeight: {
                base: "calc(100vh - 1rem)",
                md: "calc(100vh - 2rem)",
                lg: "calc(100vh - 3rem)",
              },
              "@supports (height: 100dvh)": {
                maxHeight: {
                  base: "calc(100dvh - 1rem)",
                  md: "calc(100dvh - 2rem)",
                  lg: "calc(100dvh - 3rem)",
                },
              },
            },
            // The tablet held sideways (two columns, src/index.css): as tall
            // as its content, up to the same cap, and wider.
            [TABLET_LANDSCAPE_MQ]: {
              height: "auto",
              maxHeight: { md: "calc(100vh - 2rem)", lg: "calc(100vh - 3rem)" },
              maxWidth: "1180px",
              "@supports (height: 100dvh)": {
                maxHeight: {
                  md: "calc(100dvh - 2rem)",
                  lg: "calc(100dvh - 3rem)",
                },
              },
            },
          }}
        >
          {/* The slide-* class names hook the sideways-phone rules in
              src/index.css: the fade over a scrolling answer column. */}
          <CardBody
            className="slide-card-body"
            p={{ base: 3, sm: 4, md: 8 }}
            display="flex"
            flexDirection="column"
            minH={0}
            sx={{ [SIDEWAYS_MQ]: { padding: "8px 14px 10px" } }}
          >
            {/* Title. On a phone held sideways it is as small as stays
                clearly readable: every pixel of height goes to the story. */}
            <Heading
              as="h1"
              size={{ base: "md", md: "xl" }}
              textAlign="center"
              color="pink.600"
              mb={{ base: 2, md: 6 }}
              flexShrink={0}
              sx={{
                "@media (max-height: 40em)": {
                  fontSize: "1.25rem",
                  marginBottom: "0.25rem",
                },
                [SIDEWAYS_MQ]: {
                  fontSize: "15px",
                  lineHeight: "1.2",
                  marginBottom: "4px",
                },
              }}
            >
              My Future, My Choice
            </Heading>

            {/* Meters - always a single horizontal row; stacking them
                vertically on small screens pushed the story off-screen */}
            <MetersRow />

            {/* Slide Viewer - flexes into the remaining height, scrolls only
                as a last resort (and for the game-over summary) */}
            <Box
              className="slide-scroller"
              flex="1 1 auto"
              minH={0}
              overflowY="auto"
            >
              <SlideViewer />
            </Box>
          </CardBody>
        </Card>

        <AvatarCardIfWide />
      </Flex>
    </Box>
  )
}

// Meters - always a single horizontal row; stacking them vertically on small
// screens pushed the story off-screen. Memoised: the row has no props, and
// each meter subscribes to its own values.
// On a phone held sideways each meter is one line (MeterBar.tsx) and the row
// is inset 32px, so the fixed menu and music buttons in the screen's top
// corners sit over blank card rather than over Health and Education. Only
// the small phone (640 wide) stacks them: three across would leave 40px bars.
const MetersRow = memo(function MetersRow() {
  return (
    <Grid
      templateColumns="repeat(3, 1fr)"
      gap={{ base: 2, md: 4 }}
      mb={{ base: 2, md: 6 }}
      px={{ base: 0, md: 4 }}
      flexShrink={0}
      sx={{
        "@media (max-height: 40em)": { marginBottom: "0.25rem" },
        [SIDEWAYS_MQ]: {
          gap: "12px",
          marginBottom: "6px",
          paddingInline: "32px",
        },
        [SIDEWAYS_SMALL_MQ]: {
          gridTemplateColumns: "1fr",
          gap: "2px",
          marginBottom: "4px",
        },
      }}
    >
      <Box>
        <PhysicalHealthMeter />
      </Box>
      <Box>
        <MentalHealthMeter />
      </Box>
      <Box>
        <EducationMeter />
      </Box>
    </Grid>
  )
})

// The avatar card mounts only from 2xl (1536px). Below that it is left out
// of the tree, not hidden: a hidden <img> is still fetched and decoded, and
// the point is to keep nine ~800x1920 layers out of a 3GB tablet's memory.
// ssr: false reads matchMedia on the first render, so a wide screen never
// flashes without the card and a narrow one never mounts it. Edit Character
// in the options menu is the other way to the same editor.
const WIDE_SCREEN_QUERY = "(min-width: 96em)"

const AvatarCardIfWide = () => {
  const [isWide] = useMediaQuery(WIDE_SCREEN_QUERY, {
    ssr: false,
    fallback: false,
  })
  return isWide ? <AvatarCard /> : null
}

// Character Avatar Card - beside the game on wide screens. Memoised and
// reading only the stable modal actions, so it never renders during play.
const AvatarCard = memo(function AvatarCard() {
  const { openModal } = useAvatarEditorModalActions()
  return (
    <Card
      bg="white"
      borderRadius="2xl"
      boxShadow="2xl"
      w="100%"
      maxW="420px"
      alignSelf="flex-start"
      flexShrink={0}
    >
      <CardBody p={{ base: 4, md: 6 }}>
        <Link onClick={openModal} cursor="pointer">
          <CharacterAvatar />
        </Link>
      </CardBody>
    </Card>
  )
})

export default GameSlidePage
